import { Vue } from 'vue-property-decorator'
import A from '@/utilities/alpha'
import { Question, Response } from '@/store/modules/survey-store-module'
import { SliceType } from '@shared/types/database'
import { PropType } from 'vue'
import Highcharts, { YAxisOptions } from 'highcharts'
import exportingInit from 'highcharts/modules/exporting'
import loadWordcloud from 'highcharts/modules/wordcloud'
import offlineExporting from 'highcharts/modules/offline-exporting'
import {
  generateChartSubtitle,
  getChoiceTotalsBySegment,
  getResponseTotalsForOptions,
  getSegmentCount,
  GLOBAL_CHART_HEIGHT,
  NestedResponses,
  DoubleNestedResponses,
  SegmentTotalsRecord
} from '../Utilities/ChartUtilities'
import { isOpenEnd } from '@/components/Survey/Utilities/SurveyUtilities'
import { TextAreaChartType } from '@/components/Survey/Utilities/ChartUtilities'
import { Chart } from 'highcharts-vue'
import store from '@/store'
import {
  indexChartTooltipFormatter,
  slicedChartTooltipFormatter
} from '@/components/Survey/Utilities/ChartTooltipUtilities'
import { TabData } from '@/components/Tabs/TabTypes'
import { EXPORTING_OPTIONS, generateChartFileName } from '@/utilities/highcharts-export'
import { ColorBranding } from '@/store/modules/workspace-store-module'
import { DefaultBranding } from '../Utilities/StaticColors'

export const MAX_LABEL_LENGTH = 50

exportingInit(Highcharts)
offlineExporting(Highcharts)
loadWordcloud(Highcharts)

export const BaseQuestion = Vue.extend({
  props: {
    question: {
      type: Object as PropType<Question>,
      required: true
    },
    displayTitle: {
      type: String as PropType<SliceType>,
      required: true
    },
    sliceBy: {
      type: String as PropType<SliceType>,
      required: true
    },
    isSliced: {
      type: Boolean as PropType<boolean>,
      required: true
    },
    isVariant: {
      type: Boolean as PropType<boolean>,
      required: true
    },
    slicedResponses: {
      required: true,
      type: [Object, Array] as PropType<Response[] | NestedResponses | DoubleNestedResponses>
    },
    indexResponses: {
      required: true,
      type: Array as PropType<Response[]>
    },
    filteredSlicedResponses: {
      required: true,
      type: [Object, Array] as PropType<Response[] | NestedResponses | DoubleNestedResponses>
    },
    filteredIndexResponses: {
      required: true,
      type: Array as PropType<Response[]>
    },
    chartType: {
      type: String as PropType<TextAreaChartType>,
      required: false,
      default: undefined
    },
    publicView: {
      type: Boolean as PropType<boolean>,
      required: true,
      default: false
    }
  },
  data () {
    return {
      selectedTab: Object.keys(this.slicedResponses)[0]
    }
  },
  computed: {
    typedSlicedResponses (): NestedResponses {
      return this.slicedResponses as NestedResponses
    },
    typedSlicedVariantResponses (): DoubleNestedResponses {
      return this.slicedResponses as DoubleNestedResponses
    },
    typedFilteredSlicedResponses (): NestedResponses {
      return this.filteredSlicedResponses as NestedResponses
    },
    typedFilteredSlicedVariantResponses (): DoubleNestedResponses {
      return this.filteredSlicedResponses as DoubleNestedResponses
    },
    hasOpenEnds (): boolean {
      return isOpenEnd(this.question)
    },
    tabData (): TabData[] {
      return Object.entries(this.typedSlicedVariantResponses).map(([key, responses]) => {
        return {
          title: key,
          segmentCount: getSegmentCount(responses)
        }
      })
    },
    chart (): Chart & { chart: Highcharts.Chart } {
      return this.$refs.chart as Chart & { chart: Highcharts.Chart }
    },
    chartHasData (): boolean {
      if (this.isSliced && this.isVariant) {
        return getSegmentCount(this.typedSlicedVariantResponses[this.selectedTab]) > 0
      } else if (this.isSliced) {
        return getSegmentCount(this.typedSlicedResponses) > 0
      }
      return this.indexResponses.length > 0
    },
    segmentTotals (): SegmentTotalsRecord {
      if (this.isSliced && this.isVariant) {
        return getChoiceTotalsBySegment(this.typedSlicedVariantResponses[this.selectedTab])
      } else {
        return getChoiceTotalsBySegment(this.typedSlicedResponses)
      }
    },
    indexTotals (): Record<string, number> {
      return getResponseTotalsForOptions(this.indexResponses)
    },
    chartSubtitle (): string {
      const nSize = this.isSliced && this.isVariant
        ? getSegmentCount(this.typedSlicedVariantResponses[this.selectedTab])
        : this.indexResponses.length

      const variantTab = this.isSliced && this.isVariant ? this.selectedTab : ''

      return this.isSliced ? generateChartSubtitle(nSize, this.sliceBy, variantTab) : ''
    },
    colorConfiguration (): ColorBranding {
      if (this.publicView) {
        return store.state.surveyConfiguration.testMetaDetails?.branding || DefaultBranding()
      } else {
        return store.state.workspace.branding
      }
    },
    sliceByQuestion (): Question | undefined {
      if (!this.sliceBy) return
      return store.getters.survey.questionById(this.sliceBy)
    }
  },
  watch: {
    sliceBy () {
      this.selectedTab = Object.keys(this.slicedResponses)[0]
    },
    selectedTab () {
      this.setChartRef()
    }
  },
  methods: {
    defaultChartOptions (categories?: string[]): Highcharts.Options & { exporting: Highcharts.ExportingOptions, yAxis: YAxisOptions } {
      return {
        title: {
          text: ''
        },
        chart: {
          type: 'column',
          height: GLOBAL_CHART_HEIGHT
        },
        yAxis: {
          min: 0,
          gridLineWidth: 0,
          title: {
            text: ''
          },
          labels: {
            format: '{value}%',
            style: {
              fontSize: '14px'
            }
          }
        },
        tooltip: this.isSliced || this.isVariant
          ? slicedChartTooltipFormatter(this.segmentTotals, this.question.type, this.sliceByQuestion?.type)
          : indexChartTooltipFormatter(this.indexTotals),
        credits: {
          enabled: false
        },
        exporting: {
          ...EXPORTING_OPTIONS,
          chartOptions: {
            title: {
              text: A.stripHTML(this.displayTitle)
            },
            subtitle: {
              text: this.chartSubtitle
            },
            chart: {
              events: {
                // https://github.com/highcharts/highcharts/issues/2022#issuecomment-551107773
                load: function () {
                  this.xAxis[0].update({
                    labels: {
                      formatter: function () {
                        // https://app.clubhouse.io/alpha-hq/story/6980/labeling-for-sliced-export-charts-is-broken
                        // we are passing the categories bc of the bug with labels not being updated in the scope of update method
                        // currently using highcharts 8.1, maybe highcharts 9 will fix this
                        let label = categories && categories[this.pos] ? categories[this.pos] : this.value.toString()
                        if (label.length > MAX_LABEL_LENGTH) {
                          label = label.slice(0, MAX_LABEL_LENGTH) + '...'
                        }
                        return label
                      }
                    }
                  })
                }
              }
            }
          }
        },
        xAxis: {
          gridLineWidth: 0,
          labels: {
            padding: 12,
            style: {
              whiteSpace: 'normal',
              textOverflow: 'none'
            },
            formatter: function (): string {
              let label = this.value.toString()
              const [labelTitle, nSize] = label.split('<br/>N=')
              if (labelTitle.length > MAX_LABEL_LENGTH) {
                label = labelTitle.slice(0, MAX_LABEL_LENGTH) + '...' + `${nSize ? '<br/>N=' + nSize : ''}`
              }
              return label
            }
          }
        }
      }
    },
    setChartRef () {
      const payload = {
        questionId: this.question.id,
        chartInfo: {
          exporting: this.defaultChartOptions().exporting,
          sliceBy: this.sliceBy,
          selectedTab: this.selectedTab,
          chartType: this.chartType,
          chartRef: this.chart,
          fileName: generateChartFileName(
            store.state.workspace.workspace?.name || store.state.surveyConfiguration.workspaceId,
            store.state.surveyConfiguration.testId,
            this.question.displayOrder
          )
        }
      }
      store.dispatch.livechartRefs.addRefToMap(payload)
    }
  }
})
